/**
 * /invite/:token — accept a workspace OR project invite.
 *
 * The token prefix tells us which kind it is (`inv_…` workspace, `pinv_…`
 * project), but we don't trust the prefix: we call `GET /api/v1/invites/:token`
 * which looks the invite up by hash and returns `{ kind, workspaceId,
 * projectId?, projectName?, … }`. The accept endpoint auto-attaches the user
 * to the workspace if they're not yet a member, so the same flow works for
 * invitees who have never seen the workspace before.
 *
 * FRIDAY-78: the page derives what to show from the live auth state. A cookie
 * session resolves after the first render, so nothing here may decide from
 * the `user` seen at mount. Only the invited email can accept; any other
 * signed-in account gets the mismatch state with a way to sign out.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/features/auth/authContext'
import { GoogleSignInButton } from '@/features/auth/GoogleSignInButton'
import { clearPendingInvite, rememberPendingInvite } from '@/lib/pendingInvite'

type InvitePreview =
  | {
      kind: 'workspace'
      workspaceId: string
      workspaceName: string
      email: string
      role: 'admin' | 'member' | 'viewer'
      expiresAt: string
      acceptedAt: string | null
    }
  | {
      kind: 'project'
      workspaceId: string
      workspaceName: string
      projectId: string
      projectName: string
      projectKey: string
      email: string
      role: 'lead' | 'contributor' | 'viewer'
      expiresAt: string
      acceptedAt: string | null
    }

type Accept =
  | { state: 'idle' }
  | { state: 'accepting' }
  | { state: 'error'; message: string }
  | { state: 'mismatch'; invitedEmail: string; signedInEmail: string }

export function InviteAccept() {
  const { token } = useParams({ from: '/invite/$token' })
  const { user, loading, login, logout } = useAuth()
  const navigate = useNavigate()
  const [preview, setPreview] = useState<InvitePreview | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [accept, setAccept] = useState<Accept>({ state: 'idle' })
  // One automatic attempt per token and account; retries go through the button.
  const autoAccepted = useRef<string | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [signInError, setSignInError] = useState<string | null>(null)
  const [signingIn, setSigningIn] = useState(false)

  useEffect(() => {
    let cancelled = false
    setPreview(null)
    setLoadError(null)
    rememberPendingInvite(token)
    void (async () => {
      try {
        const data = await api<InvitePreview>(`/api/v1/invites/${token}`)
        if (cancelled) return
        setPreview(data)
        setEmail((current) => current || data.email)
      } catch (e) {
        if (cancelled) return
        setLoadError(e instanceof ApiError ? e.message : 'Failed to load invite')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token])

  const expired = preview ? new Date(preview.expiresAt).getTime() < Date.now() : false
  const usable = preview !== null && !preview.acceptedAt && !expired

  async function acceptInvite(target: InvitePreview) {
    setAccept({ state: 'accepting' })
    try {
      const result = await api<{ workspaceId: string; projectId?: string }>(`/api/v1/invites/${token}/accept`, {
        method: 'POST',
      })
      clearPendingInvite()
      navigate({
        to:
          result.projectId && target.kind === 'project'
            ? `/w/${result.workspaceId}/p/${target.projectKey}`
            : `/w/${result.workspaceId}`,
      })
    } catch (e) {
      if (e instanceof ApiError && e.code === 'INVITE_EMAIL_MISMATCH') {
        const details = (e.details ?? {}) as { invitedEmail?: string; signedInEmail?: string }
        setAccept({
          state: 'mismatch',
          invitedEmail: details.invitedEmail ?? target.email,
          signedInEmail: details.signedInEmail ?? user?.email ?? '',
        })
        return
      }
      setAccept({ state: 'error', message: e instanceof ApiError ? e.message : 'Failed to accept invite' })
    }
  }

  // Accept as soon as auth has settled on a signed-in user and the invite is
  // usable. Keyed on the user id so signing in as someone else tries again.
  useEffect(() => {
    if (loading || !user || !preview || !usable) return
    const key = `${token}:${user.id}`
    if (autoAccepted.current === key) return
    autoAccepted.current = key
    void acceptInvite(preview)
  }, [loading, user, preview, usable, token]) // eslint-disable-line react-hooks/exhaustive-deps

  if (loadError) return <Notice tone="error">{loadError}</Notice>
  if (!preview || (loading && !user)) return <Notice>Loading invite…</Notice>

  const card = <InviteCard preview={preview} />

  if (preview.acceptedAt || expired) {
    return (
      <Notice tone="error" card={card}>
        {preview.acceptedAt
          ? 'This invite has already been used. Ask the person who invited you for a new one.'
          : 'This invite has expired. Ask the person who invited you for a new one.'}
        {user && (
          <Link to="/w" className="btn-ghost mx-auto mt-2 text-sm">
            Go to my workspaces
          </Link>
        )}
      </Notice>
    )
  }

  if (user) {
    if (accept.state === 'mismatch') {
      return (
        <Notice tone="error" card={card}>
          This invite is for <span className="font-medium">{accept.invitedEmail}</span>, and you are signed in as{' '}
          <span className="font-medium">{accept.signedInEmail}</span>. Sign out and sign in as{' '}
          {accept.invitedEmail}, or ask for an invite to {accept.signedInEmail}.
          <button
            type="button"
            className="btn-primary mx-auto mt-3 justify-center text-sm"
            onClick={() => void logout(`/invite/${token}`)}
          >
            Sign out
          </button>
        </Notice>
      )
    }
    return (
      <Notice tone={accept.state === 'error' ? 'error' : undefined} card={card}>
        {accept.state === 'accepting' ? 'Accepting invite…' : accept.state === 'error' ? accept.message : null}
        {accept.state !== 'accepting' && (
          <button
            type="button"
            className="btn-primary mx-auto mt-3 justify-center text-sm"
            onClick={() => void acceptInvite(preview)}
          >
            {accept.state === 'error' ? 'Try again' : 'Accept invite'}
          </button>
        )}
      </Notice>
    )
  }

  // Signed out — sign in inline, or create the account and come back.
  const next = `/invite/${token}`
  return (
    <div className="flex h-full items-center justify-center">
      <form
        className="card w-full max-w-sm space-y-3 p-6"
        onSubmit={async (e) => {
          e.preventDefault()
          setSignInError(null)
          setSigningIn(true)
          try {
            await login(email, password)
          } catch (err) {
            setSignInError(err instanceof Error ? err.message : 'Login failed')
          } finally {
            setSigningIn(false)
          }
        }}
      >
        <h1 className="text-lg font-semibold">Sign in to accept this invite</h1>
        <p className="text-xs text-surface-500">
          {preview.kind === 'project' ? 'Project' : 'Workspace'}: {prettyTarget(preview)} · invited {preview.email}
        </p>
        <input
          type="email"
          className="input"
          placeholder="you@team.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          type="password"
          className="input"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
        />
        <button type="submit" className="btn-primary w-full justify-center" disabled={signingIn}>
          {signingIn ? 'Signing in…' : 'Sign in'}
        </button>
        {signInError && <p className="text-sm text-red-500">{signInError}</p>}
        <GoogleSignInButton next={next} />
        <p className="text-center text-xs text-surface-500">
          No Friday account yet?{' '}
          <Link to="/signup" search={{ next }} className="font-medium text-accent hover:underline">
            Create one with {preview.email}
          </Link>
        </p>
      </form>
    </div>
  )
}

function Notice({
  children,
  card,
  tone,
}: {
  children: ReactNode
  card?: ReactNode
  tone?: 'error'
}) {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="w-full max-w-md space-y-3 text-center">
        {card}
        <div className={`flex flex-col text-sm ${tone === 'error' ? 'text-red-500' : 'text-surface-500'}`}>
          {children}
        </div>
      </div>
    </div>
  )
}

function InviteCard({ preview }: { preview: InvitePreview }) {
  return (
    <div className="card p-4 text-left">
      <div className="text-xs font-semibold uppercase tracking-wider text-surface-500">
        {preview.kind === 'project' ? 'Project invite' : 'Workspace invite'}
      </div>
      <div className="mt-1 text-sm font-semibold">{prettyTarget(preview)}</div>
      <div className="mt-1 text-xs text-surface-500">
        Role: {preview.role} · Invited {preview.email}
      </div>
    </div>
  )
}

function prettyTarget(p: InvitePreview): string {
  if (p.kind === 'project') return `${p.workspaceName} · ${p.projectKey} ${p.projectName}`
  return p.workspaceName
}
